/**
 * ManagementSidebar
 *
 * Sticky sidebar for the practice subscription management surface. Shows current
 * cost, line-item breakdown, payment method, billing cycle, next charge, and
 * recent invoices. Supports a "pending change" preview that switches the header
 * to "Updated Cost" and exposes a Confirm CTA.
 *
 * Presentational only — caller supplies subscription details + invoices, sidebar
 * does not fetch.
 */

import React, { useMemo } from 'react';
import { Chip } from '@jotpsych/ui';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Link,
  Typography,
} from '@mui/material';
import type {
  FullSubscriptionDetailsResponse,
  SubscriptionInvoice,
  SubscriptionLineItem,
} from '@store/generated/subscriptions';

import type { BillingInterval } from '../types';
import { formatPrice } from '../utils';

export interface ManagementSidebarPendingChange {
  /** Projected new monthly cost in cents (or monthly equivalent when annual) */
  newMonthlyCents: number;
  /**
   * Projected billing interval after Confirm. When provided and different
   * from the subscription's current interval, the sidebar:
   *   - shows the new interval in the Cycle row (with a strike-through of the
   *     current one to mirror the cost-row preview pattern),
   *   - surfaces the annual total ("Billed $X annually") when switching to year,
   *   - hides the per-line breakdown (which would be stale during preview),
   *   - relabels the Confirm button to the actual amount the user will be
   *     charged on Confirm — `$X/yr` for annual, `$X/mo` for monthly.
   * Defaults to the subscription's actual interval when omitted.
   */
  newBillingInterval?: BillingInterval;
  /** Called when the user confirms the change. May return a promise; the sidebar fires-and-forgets. */
  onConfirm: () => void | Promise<void>;
  /** Called when the user discards the pending change */
  onRevert?: () => void;
  /** Whether the confirm action is in flight */
  isConfirming?: boolean;
}

export interface ManagementSidebarProps {
  /** Current subscription details from `useGetFullSubscriptionDetailsQuery`. */
  subscription: FullSubscriptionDetailsResponse;
  /** Recent invoices (already sorted desc by date). Sidebar shows the first ~3. */
  invoices?: SubscriptionInvoice[];
  /** When provided, sidebar switches to "Updated Cost" header + Confirm button. */
  pendingChange?: ManagementSidebarPendingChange;
  /** Called when the user clicks Update / Add payment method. */
  onUpdatePaymentMethod?: () => void;
  /**
   * Pixels from the viewport top used for the sidebar's lg+ sticky offset.
   * The global Nav (~64px) + PracticeLayout header (title + tabs, ~158px)
   * both stick above this surface, so the default lands the sidebar just
   * below them. Override when mounting under a different layout chrome.
   */
  stickyTopOffsetPx?: number;
}

const RECENT_INVOICE_LIMIT = 3;

const formatShortDate = (value: string | null | undefined): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const getMonthlyCents = (item: SubscriptionLineItem, billingInterval: string): number => {
  const lineTotal = item.unit_amount * (item.quantity || 1);
  return billingInterval === 'year' ? Math.round(lineTotal / 12) : lineTotal;
};

const describePaymentMethod = (subscription: FullSubscriptionDetailsResponse): string | null => {
  const pm = subscription.payment_method;
  if (!pm?.has_payment_method) return null;
  if (pm.card?.last4) {
    const brand = pm.card.brand
      ? pm.card.brand.charAt(0).toUpperCase() + pm.card.brand.slice(1)
      : 'Card';
    return `${brand} •••• ${pm.card.last4}`;
  }
  if (pm.us_bank_account?.last4) {
    const name = pm.us_bank_account.bank_name || 'Bank';
    return `${name} •••• ${pm.us_bank_account.last4}`;
  }
  return null;
};

export const ManagementSidebar: React.FC<ManagementSidebarProps> = ({
  subscription,
  invoices,
  pendingChange,
  onUpdatePaymentMethod,
  stickyTopOffsetPx = 178,
}) => {
  const isTrialing = subscription.is_trialing;
  const billingInterval: BillingInterval =
    subscription.billing_interval === 'year' ? 'year' : 'month';
  const currency = subscription.line_items[0]?.currency ?? 'usd';

  const currentMonthlyCents = useMemo(
    () => subscription.line_items.reduce((sum, item) => sum + getMonthlyCents(item, billingInterval), 0),
    [subscription.line_items, billingInterval],
  );

  const tierItems = subscription.line_items.filter(item => item.bundle_type === 'tier');
  const addOnItems = subscription.line_items.filter(item => item.bundle_type !== 'tier');

  const hasPendingChange = !!pendingChange;
  const deltaCents = hasPendingChange
    ? pendingChange.newMonthlyCents - currentMonthlyCents
    : 0;

  // Interval-aware preview. When the pending change includes a cycle swap,
  // the sidebar should reflect the post-Confirm world: new cycle label, the
  // actual annual total (so the Confirm button can show what'll hit the card),
  // and a hidden breakdown (since line_items still carry the current cycle's
  // unit_amount and would read stale).
  const effectiveInterval: BillingInterval =
    pendingChange?.newBillingInterval ?? billingInterval;
  const isIntervalChanging =
    pendingChange?.newBillingInterval !== undefined &&
    pendingChange.newBillingInterval !== billingInterval;
  const projectedYearlyCents = hasPendingChange
    ? pendingChange.newMonthlyCents * 12
    : currentMonthlyCents * 12;

  const headerLabel = isTrialing
    ? 'After Trial'
    : hasPendingChange
      ? 'Updated Cost'
      : 'Monthly Cost';

  const paymentMethodLabel = describePaymentMethod(subscription);
  const recentInvoices = (invoices ?? []).slice(0, RECENT_INVOICE_LIMIT);

  return (
    <Card
      data-testid="management-sidebar"
      sx={{
        position: { lg: 'sticky' },
        // Offset clears the global Nav + PracticeLayout's own sticky header
        // (page title + tabs) which collectively sit above this surface and
        // would otherwise cover the top of the card. Override per-consumer
        // via the `stickyTopOffsetPx` prop if mounted under a different chrome.
        top: { lg: stickyTopOffsetPx },
        border: hasPendingChange ? '2px solid' : '1px solid',
        borderColor: hasPendingChange ? 'primary.main' : 'divider',
        borderRadius: 2,
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        {/* Header: label + Unsaved chip */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 0.5,
          }}
        >
          <Typography variant="subtitle2" color="text.secondary">
            {headerLabel}
          </Typography>
          {hasPendingChange && (
            <Chip label="Unsaved" size="xSmall" color="primary" variant="filled" />
          )}
        </Box>

        {/* Total: single number, or strikethrough old + new */}
        {hasPendingChange ? (
          <Box sx={{ mb: 0.5 }}>
            <Typography
              variant="h6"
              component="span"
              sx={{ textDecoration: 'line-through', color: 'text.secondary', mr: 1 }}
            >
              {formatPrice(currentMonthlyCents, currency)}
            </Typography>
            <Typography
              variant="h4"
              component="span"
              data-testid="management-sidebar-new-total"
              sx={{ fontWeight: 700, color: 'primary.main' }}
            >
              {formatPrice(pendingChange.newMonthlyCents, currency)}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: deltaCents < 0 ? 'success.main' : 'warning.main', fontWeight: 500 }}
            >
              {deltaCents >= 0 ? '+' : ''}
              {formatPrice(deltaCents, currency)}/mo
            </Typography>
            {effectiveInterval === 'year' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Billed {formatPrice(projectedYearlyCents, currency)} annually
              </Typography>
            )}
          </Box>
        ) : (
          <>
            <Typography
              variant="h4"
              data-testid="management-sidebar-total"
              sx={{ fontWeight: 700, mb: 0.5 }}
            >
              {formatPrice(currentMonthlyCents, currency)}
            </Typography>
            {billingInterval === 'year' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Billed {formatPrice(currentMonthlyCents * 12, currency)} annually
              </Typography>
            )}
          </>
        )}

        {/* Breakdown — hidden during an interval-change preview because the
            line_items still carry the current cycle's unit_amount and would
            read stale against the new interval. Refetches on Confirm. */}
        {!isIntervalChanging && (tierItems.length > 0 || addOnItems.length > 0) && (
          <>
            <Divider sx={{ my: 1.5 }} />
            {tierItems.map(item => (
              <Box
                key={item.stripe_subscription_item_id}
                sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}
              >
                <Typography variant="body2" color="text.secondary">
                  {item.bundle_name || 'Subscription'}
                  {item.quantity > 1 ? ` (${item.quantity})` : ''}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatPrice(getMonthlyCents(item, billingInterval), item.currency)}
                </Typography>
              </Box>
            ))}
            {addOnItems.map(item => (
              <Box
                key={item.stripe_subscription_item_id}
                sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, pl: 1.5 }}
              >
                <Typography variant="body2" color="text.secondary">
                  + {item.bundle_name || 'Add-on'}
                  {item.quantity > 1 ? ` (${item.quantity})` : ''}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatPrice(getMonthlyCents(item, billingInterval), item.currency)}
                </Typography>
              </Box>
            ))}
          </>
        )}

        {/* Confirm pending change */}
        {hasPendingChange && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              <Button
                variant="contained"
                fullWidth
                data-testid="management-sidebar-confirm"
                onClick={pendingChange.onConfirm}
                disabled={pendingChange.isConfirming}
              >
                {/* "No surprises" — when committing to annual, show the actual
                    yearly charge that'll hit the card; otherwise the monthly
                    equivalent. */}
                Confirm —{' '}
                {effectiveInterval === 'year'
                  ? `${formatPrice(projectedYearlyCents, currency)}/yr`
                  : `${formatPrice(pendingChange.newMonthlyCents, currency)}/mo`}
              </Button>
              {pendingChange.onRevert && (
                <Button
                  variant="text"
                  size="small"
                  onClick={pendingChange.onRevert}
                  disabled={pendingChange.isConfirming}
                >
                  Revert
                </Button>
              )}
            </Box>
          </>
        )}

        <Divider sx={{ my: 1.5 }} />

        {/* Payment method */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}
        >
          <Typography variant="body2">Payment</Typography>
          {paymentMethodLabel ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {paymentMethodLabel}
              </Typography>
              {onUpdatePaymentMethod && (
                <Button
                  size="small"
                  onClick={onUpdatePaymentMethod}
                  sx={{ minWidth: 0, p: 0, fontSize: '0.75rem' }}
                >
                  Change
                </Button>
              )}
            </Box>
          ) : (
            <Button
              size="small"
              variant="outlined"
              onClick={onUpdatePaymentMethod}
              sx={{ fontSize: '0.75rem' }}
            >
              Add payment method
            </Button>
          )}
        </Box>

        {/* Cycle — flips to the previewed value with the current one
            struck through when an interval change is pending, matching the
            cost row's strike-through-then-new visual. */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2">Cycle</Typography>
          {isIntervalChanging ? (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
              <Typography
                variant="body2"
                sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
              >
                {billingInterval === 'year' ? 'Annual' : 'Monthly'}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                {effectiveInterval === 'year' ? 'Annual' : 'Monthly'}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {billingInterval === 'year' ? 'Annual' : 'Monthly'}
            </Typography>
          )}
        </Box>

        {/* Next charge — hidden during trial */}
        {!isTrialing && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">Next charge</Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {formatShortDate(subscription.current_period_end)}
            </Typography>
          </Box>
        )}

        {/* Recent invoices — hidden during trial, hidden if none */}
        {!isTrialing && recentInvoices.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1, fontSize: '0.75rem' }}
            >
              Recent Invoices
            </Typography>
            {recentInvoices.map(invoice => (
              <Box
                key={invoice.id}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 0.75,
                }}
              >
                <Typography variant="caption">{formatShortDate(invoice.created)}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Chip
                    label={invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                    size="xSmall"
                    color={invoice.status.toLowerCase() === 'paid' ? 'success' : 'default'}
                    variant="soft"
                  />
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    {formatPrice(
                      invoice.total ?? invoice.amount_paid ?? invoice.amount_due,
                      currency,
                    )}
                  </Typography>
                  {invoice.invoice_pdf && (
                    <Link
                      href={invoice.invoice_pdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ fontSize: '0.65rem', fontWeight: 500 }}
                    >
                      PDF
                    </Link>
                  )}
                </Box>
              </Box>
            ))}
          </>
        )}
      </CardContent>
    </Card>
  );
};
